import React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { convertToLocalTime } from '@dev/ee-tools';
import {
    Campaign,
    CampaignStatus,
    CampaignTriggerType,
    CampaignType,
    TimingOptionEnum,
} from '@dev/ee-api/ee-api-models';
import { palette, TableRowNameLink, Typography } from '@dev/ee-components';
import { isNil } from 'lodash-es';
import { Box, Grid } from '@material-ui/core';
import { getConfigStore } from '@/store/config/actions';
import { EllipsisTypography } from '@/components/Campaigns/components/StyledComponents';
import { isCampaignsReportAvailable } from '../../..';
import { goToCampaign } from '../../helpers';

export const CampaignName = (props: { singleCampaign: Campaign; campaignRecipents }): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { singleCampaign, campaignRecipents } = props;
    const isScheduled = singleCampaign?.TriggerOptions?.Type === CampaignTriggerType.FutureScheduled;
    const showScheduleFor =
        ![CampaignStatus.Completed, CampaignStatus.Cancelled].includes(singleCampaign.Status) && isScheduled;
    const isReportAvailable = isCampaignsReportAvailable(singleCampaign);
    const timingOptions = singleCampaign?.Options?.TimingOption || TimingOptionEnum.None;
    const config = useSelector(getConfigStore);

    if (!singleCampaign) {
        return null;
    }

    const { text, caption, date } =
        [
            {
                text: t('sent'),
                date: convertToLocalTime(
                    isScheduled ? singleCampaign.TriggerOptions.DateToSendOn : singleCampaign.DateAdded
                ),
                requiredCampaignStatus: CampaignStatus.Completed,
            },
            {
                text: t('created'),
                date: convertToLocalTime(singleCampaign.DateAdded),
                additionalRequirements: singleCampaign.Type === CampaignType.Automated,
            },
            {
                caption: t('werecollectingyourabtestresults'),
                requiredCampaignStatus: CampaignStatus.Sending,
                additionalRequirements: singleCampaign.Type === CampaignType.AbSplit,
            },
            {
                caption:
                    timingOptions === TimingOptionEnum.EngagedContactFirst
                        ? t('sendingtoengagedcontactfirst')
                        : t('optimizeddelivery_24'),
                requiredCampaignStatus: CampaignStatus.Sending,
                additionalRequirements: timingOptions !== TimingOptionEnum.None && !isScheduled,
            },
            {
                text: t('scheduledfor'),
                date: convertToLocalTime(singleCampaign?.TriggerOptions?.DateToSendOn),
                additionalRequirements:
                    isScheduled && showScheduleFor && singleCampaign.Status !== CampaignStatus.Sending,
            },

            {
                text: t('canceled'),
                date: convertToLocalTime(singleCampaign.LastActivity || singleCampaign.DateAdded),
                requiredCampaignStatus: CampaignStatus.Cancelled,
            },
        ].find(
            (op) =>
                (isNil(op.requiredCampaignStatus) || op.requiredCampaignStatus === singleCampaign.Status) &&
                (isNil(op.additionalRequirements) || op.additionalRequirements)
        ) || {};

    return (
        <div>
            <Typography variant="caption" color={palette.gray[7]} className="campaign-name">
                <TableRowNameLink
                    onClick={(ev): void => {
                        ev.stopPropagation();
                        goToCampaign(dispatch, config, singleCampaign, isReportAvailable);
                    }}
                >
                    {singleCampaign.Name}
                </TableRowNameLink>
            </Typography>

            <EllipsisTypography variant="body1" bodyVariant="help">
                {campaignRecipents}
            </EllipsisTypography>

            {text && date && (
                <Grid container>
                    <Typography variant="body1" bodyVariant="help">
                        {`${text}:`}
                    </Typography>
                    <Box ml="5px" />
                    <Typography variant="caption">{date}</Typography>
                </Grid>
            )}
            {caption && (
                <EllipsisTypography variant="body1" bodyVariant="help">
                    {caption}
                </EllipsisTypography>
            )}
        </div>
    );
};
